
import { Component, Prop, Mixins } from 'vue-property-decorator';
import {
  Dialog,
  Form,
  FormItem,
  Select,
  Option,
  Input,
  InputNumber,
  Button,
} from 'element-ui';
import { IResource } from '@/types/course.d';
import api from '@/api/course';
import FormMixin from './formMxin';

@Component({
  components: {
    Dialog,
    Form,
    FormItem,
    Select,
    Option,
    Input,
    InputNumber,
    Button,
  },
})
export default class ManageBusModal extends Mixins(FormMixin) {
  @Prop({
    required: true,
    default: false,
  }) visible!: boolean;

  @Prop({
    required: true,
    default: false,
  }) title!: string;

  @Prop({
    required: false,
  }) oldForm!: IResource;

  @Prop({ type: Boolean, default: false }) questionOnly!: boolean;

  /** 输入或关闭使旧查询失效，避免旧响应覆盖新输入。 */
  invalidateQuestionQuery() {
    this.queryVersion += 1;
    this.questionLoading = false;
    this.questionCandidate = null;
    this.questionError = '';
  }

  private questionNumber: string = '';

  private questionEditing: boolean = false;

  private queryVersion: number = 0;

  private questionLoading: boolean = false;

  private questionError: string = '';

  private questionCandidate: IResource['linkedQuestion'] = null;

  /** 按展示题号精确查询所有可关联编程题；无入参，返回查询完成 Promise。 */
  async searchQuestion() {
    if (this.questionLoading || this.loading) return;
    this.queryVersion += 1;
    const version = this.queryVersion; // 本次查询序号，用于丢弃过期响应。
    const number = this.questionNumber.trim(); // 用户输入的完整展示题号。
    this.questionCandidate = null;
    this.questionError = '';
    if (!number) { this.questionError = '请输入题号'; return; }
    this.questionLoading = true;
    try {
      const result: any = await api.getResourceQuestions(number);
      // 题号是字符串；主键只取查询结果，绝不把用户输入当作主键。
      // 可见状态不限制关联，服务端统一排除个人题、团队题、已删除题和非编程题。
      const rows = (result || []).filter((q: any) => q.questionId === number
        && q.type === 4 && !q.delFlag);
      if (version !== this.queryVersion || number !== this.questionNumber.trim()) return;
      if (rows.length !== 1 || result.length > 1) {
        this.questionError = '未找到唯一可关联的编程题，请核对题号';
        return;
      }
      const row = rows[0];
      if (typeof row.pid === 'number' && !Number.isSafeInteger(row.pid)) {
        this.questionError = '题目主键超出浏览器安全范围，请联系管理员';
        return;
      }
      this.questionCandidate = {
        pid: String(row.pid), questionId: row.questionId, title: row.title, type: row.type,
      };
    } catch (error) {
      if (version === this.queryVersion) this.questionError = '查询失败，请重试；已输入的题号已保留';
    } finally {
      if (version === this.queryVersion) this.questionLoading = false;
    }
  }

  /** 明确选择查询结果；无入参，返回 void。 */
  selectQuestion() {
    if (!this.questionCandidate
      || this.questionCandidate.questionId !== this.questionNumber.trim()) return;
    this.$set(this.form, 'ojQuestionPid', this.questionCandidate.pid);
    this.$set(this.form, 'linkedQuestion', this.questionCandidate);
    this.questionEditing = false;
    this.questionCandidate = null;
  }

  /** 仅清除关联，不改变源码附件；无入参，返回 void。 */
  clearQuestion() {
    this.queryVersion += 1;
    this.questionLoading = false;
    this.questionEditing = false;
    this.$set(this.form, 'ojQuestionPid', null);
    this.$set(this.form, 'linkedQuestion', null);
    this.questionCandidate = null;
    this.questionNumber = '';
    this.questionError = '';
  }

  cancelQuestionEdit() {
    this.queryVersion += 1;
    this.questionLoading = false;
    this.questionNumber = this.form.linkedQuestion ? this.form.linkedQuestion.questionId : '';
    this.questionCandidate = null;
    this.questionError = '';
    this.questionEditing = false;
  }

  private types: { value: number, label: string }[] = [
    { label: 'Scratch3源码模式', value: 11 },
    { label: 'Scratch3演示模式', value: 12 },
    { label: 'Python源码模式', value: 7 },
    { label: 'Python演示模式', value: 8 },
    { label: 'C++源码模式', value: 14 },
    { label: 'C++演示模式', value: 15 },
    { label: 'Microbit源码模式', value: 9 },
    { label: 'Microbit演示模式', value: 10 },
    { label: '教案', value: 5 },
    { label: '演示课件', value: 6 },
    { label: 'ZIP', value: 13 },
    { label: 'HTML教学工具', value: 16 },
    { label: '视频', value: 3 },
    { label: '小视频', value: 4 },
    { label: 'Scratch源码模式', value: 1 },
    { label: 'Scratch演示模式', value: 2 },
  ];

  private users: { value: number, label: string }[] = [{ value: 1, label: '老师/学生' }, { value: 2, label: '老师' }, { value: 3, label: '仅学生' }];

  /** 初始化编辑回显或新建表单；无入参，返回 void。 */
  mounted() {
    if (this.questionOnly || this.title.includes('修改')) this.form = Object.assign({}, this.oldForm);
    else this.form.stepPid = this.oldForm.stepPid;
    this.questionNumber = this.form.linkedQuestion ? this.form.linkedQuestion.questionId : '';
    if (this.questionOnly) this.questionEditing = true;
  }

  /** 保存基本信息和明确选择的关联；title 为弹窗标题，返回 void。 */
  submit(title: string) {
    if (this.questionLoading || this.loading) return;
    // 只修改题号文本不会隐式换绑；避免未确认的候选被误保存。
    const pendingEdit = this.questionEditing && !!this.form.ojQuestionPid;
    const unconfirmedNumber = this.questionNumber && (!this.form.linkedQuestion
      || this.form.linkedQuestion.questionId !== this.questionNumber);
    if (this.form.resourceType === 14 && (pendingEdit || unconfirmedNumber)) {
      this.questionError = '请查询并确认关联，或取消修改后保存';
      return;
    }
    if (this.form.resourceType !== 14) this.clearQuestion();
    if (this.questionOnly) {
      if (!this.form.ojQuestionPid || this.questionEditing) return;
      this.loading = true;
      api.changeResourceQuestion({
        pid: this.oldForm.pid,
        expectedOjQuestionPid: this.oldForm.ojQuestionPid == null
          ? null : String(this.oldForm.ojQuestionPid),
        ojQuestionPid: String(this.form.ojQuestionPid),
      }).then((result: any) => {
        this.$emit('question-saved', result);
        this.loading = false;
        this.reset(true);
      }).catch(() => {
        this.loading = false;
        this.questionError = '保存失败，原关联未变；若关联已被他人修改，请刷新列表后重试';
      });
      return;
    }
    this.loading = true;
    this.$refs.form.validate((valid: boolean) => {
      if (valid) {
        if (title.includes('修改')) {
          api.editRescource(this.form).then(() => {
            this.reset(true, true);
          /* eslint-disable no-return-assign */
          }).catch(() => (this.loading = false));
        } else {
          api.addRescource(this.form).then(() => {
            this.reset(true, true);
          /* eslint-disable no-return-assign */
          }).catch(() => (this.loading = false));
        }
      } else this.loading = false;
    });
  }

  // 重置表单
  reset(close: boolean = false, refrsh: boolean = false) {
    if (this.questionOnly && this.loading) return;
    this.invalidateQuestionQuery();
    this.loading = false;
    if (close) this.$emit('update:visible', false);
    if (refrsh) this.$emit('refrsh', this.form.stepPid);
    this.resetForm();
    this.clearQuestion();
  }
}
